import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Lightbulb } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'new', label: '✨ New' },
  { id: 'enhancement', label: '🔧 Enhancement' },
  { id: 'broken', label: '🐛 Something broken?' },
  { id: 'other', label: '💬 Other' },
];

const SuggestionBoxModal = ({ isOpen, onClose }) => {
  const [category, setCategory] = useState('new');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {ok, msg}

  if (!isOpen) return null;

  const submit = async () => {
    if (text.trim().length < 3) {
      setResult({ ok: false, msg: 'Give us a little more detail!' });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tools/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
        },
        body: JSON.stringify({ suggestion: text.trim(), category, source: 'community' })
      });
      const d = await r.json();
      if (r.ok) {
        setResult({ ok: true, msg: '💡 Thanks! Your idea is in the box.' });
        setText('');
        setTimeout(() => { setResult(null); onClose(); }, 3000);
      } else {
        setResult({ ok: false, msg: d.detail || 'Something went wrong — try again' });
      }
    } catch (e) {
      setResult({ ok: false, msg: 'Network error — try again' });
    }
    setSubmitting(false);
  };

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      data-testid="suggestion-box-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="suggestion-box-modal"
        style={{
          width: '100%', maxWidth: '420px',
          background: 'linear-gradient(160deg, #12122b 0%, #1a1a3e 100%)',
          border: '1px solid rgba(250, 204, 21, 0.35)', borderRadius: '14px',
          boxShadow: '0 0 40px rgba(250, 204, 21, 0.1)', overflow: 'hidden'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.9rem 1rem', borderBottom: '1px solid rgba(250, 204, 21, 0.2)'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#facc15', fontWeight: 700, fontSize: '0.95rem' }}>
            <Lightbulb size={18} />
            Suggestion Box
          </span>
          <button
            onClick={onClose}
            data-testid="suggestion-box-close"
            style={{
              background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', cursor: 'pointer'
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '1rem' }}>
          <p style={{ color: '#9ca3af', fontSize: '0.82rem', margin: '0 0 12px' }}>
            Got an idea to make BEATSEEK better? Drop it in the box — every idea goes straight to the team.
          </p>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="suggestion-category-select"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '9px 10px', marginBottom: '10px',
              borderRadius: '8px', background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(250, 204, 21, 0.3)', color: '#fff', fontSize: '0.85rem'
            }}
          >
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1000}
            placeholder="What's your idea?"
            data-testid="suggestion-text-input"
            style={{
              width: '100%', boxSizing: 'border-box', minHeight: '110px', padding: '10px',
              borderRadius: '8px', background: 'rgba(0,0,0,0.5)', resize: 'vertical',
              border: '1px solid rgba(250, 204, 21, 0.3)', color: '#fff', fontSize: '0.87rem',
              fontFamily: 'inherit'
            }}
          />
          <div style={{ textAlign: 'right', fontSize: '0.68rem', color: '#565b70', margin: '4px 0 10px' }}>
            {text.length}/1000
          </div>

          {result && (
            <div
              data-testid="suggestion-result-msg"
              style={{
                padding: '8px 12px', borderRadius: '8px', fontSize: '0.8rem', marginBottom: '10px',
                background: result.ok ? 'rgba(34,197,94,0.12)' : 'rgba(255,51,51,0.12)',
                border: result.ok ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(255,51,51,0.4)',
                color: result.ok ? '#4ade80' : '#ff6b6b'
              }}
            >
              {result.msg}
            </div>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="suggestion-submit-btn"
            style={{
              width: '100%', padding: '11px', borderRadius: '8px', border: 'none',
              background: 'linear-gradient(135deg, #facc15, #eab308)', color: '#1a1a2e',
              fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer',
              opacity: submitting ? 0.6 : 1
            }}
          >
            {submitting ? 'Sending...' : '💡 Drop it in the box'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SuggestionBoxModal;
